import React, { useEffect, useState } from "react";
import axios from "axios";
import SEO from "../common/SEO";
import Layout from "../common/Layout";
import BreadcrumbOne from "../elements/breadcrumb/BreadcrumbOne";
import { useMemo } from "react";
import FooterTwo from "../common/footer/FooterTwo";
import Copyright from "../common/footer/Copyright";
import { FiCheckCircle, FiFacebook, FiLinkedin, FiCopy } from "react-icons/fi";
import { useRef } from "react";


const BlogPost = ({ match: { params: { source, slug } } }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        let endpoint = "";
        if (source === "news") {
          endpoint = `https://whatson.guide/wp-json/wp/v2/posts?slug=${slug}&_embed`;
        } else if (source === "blog") {
          endpoint = `https://new.whatson.agency/wp-json/wp/v2/posts?slug=${slug}&_embed`;
        } else {
          throw new Error("Invalid source");
        }
        const resp = await axios.get(endpoint);
        const dataArr = Array.isArray(resp.data) ? resp.data : [resp.data];
        const data = dataArr[0];
        if (!data) {
          throw new Error("Post not found");
        }

        const extractFirstImageSrc = (html) => {
          if (!html) return "";
          const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
          return match ? match[1] : "";
        };

        let featuredImage = "";
        if (data._embedded && data._embedded["wp:featuredmedia"] && data._embedded["wp:featuredmedia"][0]) {
          featuredImage = data._embedded["wp:featuredmedia"][0].source_url;
        }
        if (!featuredImage) {
          featuredImage = extractFirstImageSrc(data.content?.rendered || "");
        }

        let authorName = "Unknown Author";
        if (data._embedded && data._embedded.author && data._embedded.author[0]) {
          authorName = data._embedded.author[0].name;
        }

        const formatDate = (dateString) => {
          const d = new Date(dateString);
          return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
        };

        setPost({
          id: data.id,
          slug: data.slug,
          title: data.title?.rendered || "",
          content: data.content?.rendered || "",
          date: data.date ? formatDate(data.date) : undefined,
          author: authorName,
          featured_image: featuredImage
        });
        setLoading(false);
      } catch (err) {
        setError(err.message || "Failed to load post");
        setLoading(false);
      }
    };
    fetchPost();
  }, [source, slug]);

  const shareUrl = useMemo(() => {
    const base = window.location.origin + (process.env.PUBLIC_URL || "");
    const path = `/blog/${source}/${post?.slug || slug}`;
    return base + path;
  }, [source, post?.slug, slug]);

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    if (!contentRef.current) return;
    const placeholder = "https://via.placeholder.com/1200x600?text=Image+not+available";
    const imgs = contentRef.current.querySelectorAll("img");
    const onErr = (e) => {
      const el = e.target;
      if (el && el.src !== placeholder) {
        el.src = placeholder;
      }
    };
    imgs.forEach((img) => {
      img.style.maxWidth = "100%";
      img.style.height = "auto";
      img.style.paddingLeft = "16px";
      img.style.paddingRight = "16px";
      img.addEventListener("error", onErr);
    });
    return () => {
      imgs.forEach((img) => {
        img.removeEventListener("error", onErr);
      });
    };
  }, [post]);

  if (loading) {
    return <p style={{ textAlign: "center", padding: "40px" }}>Loading...</p>;
  }

  if (error) {
    return <p style={{ textAlign: "center", padding: "40px" }}>Error: {error}</p>;
  }

  return (
    <>
      <SEO title={post?.title || "Post"} />
      <Layout>
        <BreadcrumbOne
          title={post?.title || "Post"}
          rootUrl="/"
          parentUrl="Home"
          currentUrl={source === "news" ? "News Post" : "Blog Post"}
        />
        <div className="main-content">
          <div className="rn-blog-area rn-section-gap">
            <div className="container">
              <div className="row">
                <div className="col-lg-10 mx-auto">
                  <div className="rn-card box-card-style-default blog-post-card">
                    <div style={{ marginBottom: "16px" }}>
                      <button
                        onClick={() => window.history.back()}
                        className="btn-default btn-border"
                        style={{ padding: "10px 18px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                      >
                        Back
                      </button>
                    </div>
                    {post?.featured_image && (
                      <div style={{ marginBottom: "24px", paddingLeft: "16px", paddingRight: "16px" }}>
                        <div style={{ width: "100%", aspectRatio: "16 / 9", borderRadius: "8px", overflow: "hidden", backgroundColor: "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            style={{ width: "70%", height: "100%", objectFit: "contain" }}
                          />
                        </div>
                      </div>
                    )}
                    <h2 style={{ marginBottom: "8px", paddingLeft: "16px", paddingRight: "16px" }}>{post?.title}</h2>
                    {(post?.date || post?.author) && (
                      <div style={{ fontSize: "14px", marginBottom: "16px", color: "#666", paddingLeft: "16px", paddingRight: "16px" }}>
                        {post?.date && <span>Published: {post.date}</span>}
                        {post?.author && <span style={{ marginLeft: "12px" }}>By: {post.author}</span>}
                      </div>
                    )}
                    <div
                      className="blog-single-content"
                      style={{ paddingLeft: "16px", paddingRight: "16px" }}
                      ref={contentRef}
                      dangerouslySetInnerHTML={{ __html: post?.content || "" }}
                    />
                    <div style={{ marginTop: "24px", textAlign: "center" }}>
                      <div className="share-buttons" style={{ display: "inline-flex", gap: "10px", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
                        <a
                          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-default btn-border btn-small"
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                          aria-label="Share on Facebook"
                        >
                          Facebook
                        </a>
                        <a
                          href={`https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post?.title || "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-default btn-border btn-small"
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                          aria-label="Share on X"
                        >
                          X
                        </a>
                        <a
                          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-default btn-border btn-small"
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                          aria-label="Share on LinkedIn"
                        >
                          LinkedIn
                        </a>
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(post?.title || "")}%20${encodeURIComponent(shareUrl)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-default btn-border btn-small"
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                          aria-label="Share on WhatsApp"
                        >
                          WhatsApp
                        </a>
                        <button
                          onClick={handleCopyLink}
                          className="btn-default btn-border btn-small"
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                          title="Copy link"
                          aria-label="Copy link to clipboard"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
      {/* Share buttons use default btn-default styles for text-only appearance */}
      {copied && (
        <>
          <style>
            {`
              @keyframes toastFade {
                0% { opacity: 0; transform: translate(-50%, 10px); }
                10% { opacity: 1; transform: translate(-50%, 0); }
                90% { opacity: 1; transform: translate(-50%, 0); }
                100% { opacity: 0; transform: translate(-50%, -10px); }
              }
            `}
          </style>
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "fixed",
              left: "50%",
              bottom: "24px",
              transform: "translateX(-50%)",
               background: "linear-gradient(90deg, #d40606ff, #ed3a9cff)",
              color: "#fff",
              padding: "10px 16px",
              borderRadius: "10px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              fontWeight: 600,
              animation: "toastFade 2s ease-in-out forwards",
              zIndex: 9999
            }}
          >
            <FiCheckCircle size={18} />
            Link copied to clipboard
          </div>
        </>
      )}
      <FooterTwo />
      <Copyright />
      <style>
        {`
          .share-buttons .btn-small { min-width: 100px; }
          @media (max-width: 576px) {
            .share-buttons { gap: 8px; }
            .share-buttons .btn-small { flex: 1 1 calc(50% - 8px); padding: 8px 10px; }
          }
          .blog-post-card { transition: background-color .3s ease, color .3s ease; background-color: #0f1318; color: #e6e6e6; }
          .blog-single-content { border-radius: 8px; transition: background-color .3s ease, color .3s ease; background-color: #0f1318; color: #e6e6e6; }
          .blog-single-content a { color: #7cc4ff; }
          body.light .blog-post-card { background-color: #ffffff; color: #1c1c1c; }
          body.light .blog-single-content { background-color: #ffffff; color: #1c1c1c; }
          body.light .blog-single-content a { color: #0b6bcb; }
        `}
      </style>
    </> 
  );
};

export default BlogPost;
